import { useCallback, useState } from 'react';
import styled from 'styled-components';

import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';

import MoreContentRequester from 'components/MoreContentRequester';
import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import StandardModal from 'components/shared/StandardModal';
import useIsMobile from 'hooks/useIsMobile';
import useTruncatedContent from 'hooks/useTruncatedContent';
import { dateAtTime } from 'lib/date';
import KeyDetailsActivity from 'models/activities/KeyDetails';
import Activity from 'models/Activity';
import { Paginated } from 'models/Paginated';
import { PERMISSIONS } from 'models/Profile';
import { colors } from 'styles/theme/colors';
import { devices } from 'styles/theme/devices';
import { BodySmall, Label } from 'styles/typography';
import ChevronDown from 'svg/ChevronDown';
import PencilIcon from 'svg/PencilIcon';
import PinIcon from 'svg/PinIcon';

export type KeyDetailsProps = {
  editable?: boolean;
  entries?: KeyDetailsActivity[];
  onPost?: (content: string) => Promise<void> | void;
  query?: UseInfiniteQueryResult<InfiniteData<Paginated<Activity>, unknown>, Error>;
  submitting?: boolean;
};

const SHOW_MORE_THRESHOLD = 200;

function KeyDetails({ editable = true, entries = [], onPost, query, submitting = false }: KeyDetailsProps) {
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const current = entries[0];
  const content = current?.data.plaintext;

  const { containerRef, needsShowMore, truncatedContent } = useTruncatedContent(
    content,
    SHOW_MORE_THRESHOLD,
    !collapsed
  );

  const handleUpdate = useCallback(() => {
    setEditValue(content ?? '');
    setEditing(true);
    setCollapsed(false);
  }, [content]);

  const handleCancel = useCallback(() => {
    setEditing(false);
    setEditValue('');
  }, []);

  const handlePost = useCallback(async () => {
    await onPost?.(editValue);
    setEditing(false);
    setEditValue('');
  }, [editValue, onPost]);

  const timestamp = current?.createdAt ? dateAtTime(current.createdAt) : null;
  const authorLine = current?.createdBy?.fullName ?? '';
  const locationLine = timestamp ? `on ${timestamp}` : null;

  const hasContent = !!content;

  return (
    <Container data-cy='key-details' ref={containerRef}>
      <Header>
        <HeaderLeft>
          <CollapseToggle
            onClick={() => {
              if (!collapsed && editing) {
                handleCancel();
              }
              setCollapsed(!collapsed);
            }}>
            <StyledChevron $collapsed={collapsed} />
            <Title>Key Details</Title>
            <StyledPin />
          </CollapseToggle>
          {entries.length > 0 && !editing && !isMobile && (
            <ViewHistoryLink data-cy='key-details-view-history' onClick={() => setShowHistoryModal(true)}>
              View History
            </ViewHistoryLink>
          )}
        </HeaderLeft>
        {editable && (
          <AccessGuard accessor={PERMISSIONS.activityKeyDetailsCreate}>
            <HeaderActions>
              <UpdateButton
                data-cy='key-details-update'
                $disabled={editing}
                onClick={editing ? undefined : handleUpdate}>
                <PencilIcon width={12} height={12} color={colors.blue100} />
                Update
              </UpdateButton>
            </HeaderActions>
          </AccessGuard>
        )}
      </Header>

      {!collapsed && (
        <Content>
          {editing ? (
            <EditingContainer>
              <TextArea
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder='Start typing here to add a key detail...'
                disabled={submitting}
                autoFocus
              />
              <EditActions>
                <SmallButton variant='ghost' size='xsmall' onClick={handleCancel} disabled={submitting}>
                  Cancel
                </SmallButton>
                <SmallButton size='xsmall' onClick={handlePost} disabled={!editValue.trim()} loading={submitting}>
                  Post
                </SmallButton>
              </EditActions>
            </EditingContainer>
          ) : hasContent ? (
            <>
              <ContentText data-content-text>
                {needsShowMore && !showMore ? (
                  <>
                    {truncatedContent}…{' '}
                    <ShowMoreToggle as='span' $inline onClick={() => setShowMore(true)}>
                      Show More
                    </ShowMoreToggle>
                  </>
                ) : (
                  content
                )}
              </ContentText>
              {needsShowMore && showMore && (
                <ShowMoreToggle onClick={() => setShowMore(false)}>Show Less</ShowMoreToggle>
              )}
              {(authorLine || locationLine) && (
                <Timestamp>
                  {authorLine}
                  {locationLine && ` ${locationLine}`}
                </Timestamp>
              )}
            </>
          ) : (
            <EmptyState>There are no key details to display yet.</EmptyState>
          )}
        </Content>
      )}

      {showHistoryModal && (
        <StandardModal title='Key Details History' onCancel={() => setShowHistoryModal(false)}>
          <HistoryList>
            {entries.map((entry) => (
              <HistoryEntry key={entry.id}>
                <HistoryContent>{entry.data.plaintext}</HistoryContent>
                <HistoryTimestamp>
                  {entry.createdBy?.fullName}
                  {entry.createdAt && ` on ${dateAtTime(entry.createdAt)}`}
                  {entry.createdByGroup?.name && ` at ${entry.createdByGroup.name}`}
                </HistoryTimestamp>
              </HistoryEntry>
            ))}
            {query && <MoreContentRequester resource={query} />}
          </HistoryList>
          <StyledButtonGroup>
            <Button onClick={() => setShowHistoryModal(false)}>Close</Button>
          </StyledButtonGroup>
        </StandardModal>
      )}
    </Container>
  );
}

const Container = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  padding: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CollapseToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const StyledChevron = styled(ChevronDown)<{ $collapsed: boolean }>`
  transform: ${({ $collapsed }) => ($collapsed ? 'rotate(90deg)' : 'rotate(180deg)')};
  transform-origin: center;
  transition: transform 0.2s ease;
  width: 8px;
  height: 8px;

  path {
    fill: ${colors.black100};
    stroke: ${colors.black100};
    stroke-width: 0.5px;
  }
`;

const StyledPin = styled(PinIcon).attrs({ width: 12, height: 12 })`
  color: ${colors.black100};
  flex-shrink: 0;
`;

const Title = styled.span`
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  line-height: 20px;
  color: ${colors.black100};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ViewHistoryLink = styled.span`
  font-size: 10px;
  color: ${colors.blue100};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const UpdateButton = styled.span<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: ${colors.blue100};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.25 : 1)};

  &:hover {
    ${({ $disabled }) => !$disabled && 'text-decoration: underline;'}
  }
`;

const Content = styled.div`
  margin-top: 12px;
`;

const ContentText = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: ${colors.black100};
  white-space: pre-wrap;
`;

const ShowMoreToggle = styled.span<{ $inline?: boolean }>`
  font-size: 10px;
  line-height: 18px;
  color: ${colors.blue100};
  cursor: pointer;
  ${({ $inline }) => ($inline ? 'margin-left: 4px;' : '')}

  &:hover {
    text-decoration: underline;
  }
`;

const Timestamp = styled.span`
  display: block;
  font-size: 10px;
  line-height: 14px;
  color: ${colors.black50};
  margin-top: 8px;
`;

const EmptyState = styled.span`
  font-size: 12px;
  line-height: 18px;
  color: ${colors.black50};
`;

const EditingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  font-size: 14px;
  line-height: 20px;
  font-family: inherit;
  resize: none;
  color: ${colors.black100};

  &::placeholder {
    color: ${colors.black25};
  }

  &:focus {
    outline: none;
    border-color: ${colors.blue100};
  }
`;

const SmallButton = styled(Button)`
  padding: 4px 16px;
`;

const EditActions = styled(ButtonGroup)`
  justify-content: flex-end;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  overflow-y: auto;
  max-height: 400px;

  @media ${devices.desktop} {
    max-height: 50vh;
  }
`;

const HistoryEntry = styled.div`
  padding-bottom: 16px;
  border-bottom: 1px solid ${colors.black10};

  &:last-child {
    border-bottom: none;
  }
`;

const HistoryContent = styled(BodySmall)`
  color: ${colors.black100};
  white-space: pre-wrap;
`;

const HistoryTimestamp = styled(Label)`
  display: block;
  color: ${colors.black50};
  margin-top: 4px;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  justify-content: flex-end;
  margin-top: 16px;
`;

export default KeyDetails;
